import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type Session from '../services/session';
import type ShareIntentService from '../services/share-intent';
import type PushNotifications from '../services/push-notifications';
import type OfflineQueue from '../services/offline-queue';
import type Notifications from '../services/notifications';
import Icon from './icon';
import FilmIcon from '~icons/lucide/film';
import ImageIcon from '~icons/lucide/image';
import LinkIcon from '~icons/lucide/link';
import ClipboardPasteIcon from '~icons/lucide/clipboard-paste';
import UploadIcon from '~icons/lucide/upload';
import LoaderIcon from '~icons/lucide/loader-2';
import { apiFetch } from '../fetch';
import { Capacitor } from '@capacitor/core';
import { type UrlPreview, fetchUrlPreview } from '../utils/url-preview';

interface SubmissionResponse {
  data?: {
    attributes?: {
      sourceId?: string | null;
      queued?: boolean;
    };
  };
  errors?: Array<{ detail?: string }>;
}

export interface ExtractFormSignature {
  Args: {
    /** Optional callback invoked on successful submission, before navigation */
    onSuccess?: () => void;
  };
}

export default class ExtractForm extends Component<ExtractFormSignature> {
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare shareIntent: ShareIntentService;
  @service declare pushNotifications: PushNotifications;
  @service declare offlineQueue: OfflineQueue;
  @service declare notifications: Notifications;

  @tracked url = '';
  @tracked activeTab: 'url' | 'image' = 'url';
  @tracked selectedFile: File | null = null;
  @tracked isSubmitting = false;
  @tracked error: string | null = null;
  @tracked preview: UrlPreview | null = null;
  @tracked isDragOver = false;
  @tracked imageObjectUrl: string | null = null;

  private previewTimer: ReturnType<typeof setTimeout> | null = null;

  // Track if current submission is from share intent (don't request permission)
  isShareIntentSubmission = false;

  constructor(owner: Owner, args: ExtractFormSignature['Args']) {
    super(owner, args);

    // Check for shared image intent first
    const sharedImage = this.shareIntent.consumeImage();
    if (sharedImage) {
      this.isShareIntentSubmission = true;
      this.activeTab = 'image';
      void this.doNativeImageSubmit(sharedImage.uri, sharedImage.type);
      return;
    }

    // Check for shared URL intent
    const sharedUrl = this.shareIntent.consume();
    if (sharedUrl) {
      this.isShareIntentSubmission = true;
      this.url = sharedUrl;
      void this.doSubmit();
    }
  }

  @action
  switchToUrl() {
    this.activeTab = 'url';
    this.error = null;
  }

  @action
  switchToImage() {
    this.activeTab = 'image';
    this.error = null;
  }

  @action
  updateUrl(event: Event) {
    this.url = (event.target as HTMLInputElement).value;
    this.schedulePreview(this.url);
  }

  private schedulePreview(url: string): void {
    this.preview = null;

    if (this.previewTimer !== null) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }

    if (!url.startsWith('http')) return;

    this.previewTimer = setTimeout(() => {
      void this.loadPreview(url);
    }, 600);
  }

  private async loadPreview(url: string): Promise<void> {
    const result = await fetchUrlPreview(url);
    // Only apply the result when the URL hasn't changed since we started.
    if (this.url === url) {
      this.preview = result;
    }
  }

  override willDestroy(): void {
    super.willDestroy();
    if (this.previewTimer !== null) {
      clearTimeout(this.previewTimer);
    }
  }

  @action
  async pasteUrl() {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        this.url = text;
        this.error = null;
        this.schedulePreview(text);
      }
    } catch {
      // Clipboard access denied or not available — silently ignore
    }
  }

  @action
  handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.setSelectedFile(file);
  }

  @action
  handleDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  @action
  handleDragLeave(event: DragEvent) {
    // Only clear when leaving the drop zone entirely (not entering a child)
    const target = event.currentTarget as HTMLElement;
    if (!target.contains(event.relatedTarget as Node | null)) {
      this.isDragOver = false;
    }
  }

  @action
  handleDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files[0] ?? null;
    if (file && file.type.startsWith('image/')) {
      this.setSelectedFile(file);
    } else if (file) {
      this.error = 'Please drop an image file (PNG, JPEG, WebP, or GIF)';
    }
  }

  private setSelectedFile(file: File | null) {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
    }
    this.selectedFile = file;
    this.imageObjectUrl = file ? URL.createObjectURL(file) : null;
    this.error = null;
  }

  @action
  async submit(event: Event) {
    event.preventDefault();
    if (this.activeTab === 'image') {
      await this.doImageSubmit();
    } else {
      await this.doSubmit();
    }
  }

  async doSubmit() {
    if (!this.url) return;

    if (!this.session.isAuthenticated) {
      this.router.transitionTo('login');
      return;
    }

    // Cancel any pending preview fetch and clear the card before submitting.
    if (this.previewTimer !== null) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
    this.preview = null;

    this.isSubmitting = true;
    this.error = null;

    // Request push permission for manual submissions (not share intent)
    if (!this.isShareIntentSubmission) {
      const userId = this.session.user?.id;
      if (userId) {
        void this.pushNotifications.requestPermission(userId);
      }
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/vnd.api+json',
      };
      const authHeader = this.session.authHeader;
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }

      const response = await apiFetch('/api/submissions', {
        method: 'POST',
        headers,
        body: JSON.stringify({ url: this.url }),
      });

      const data = (await response.json()) as SubmissionResponse;

      if (!response.ok) {
        throw new Error(data.errors?.[0]?.detail ?? 'Failed to submit URL');
      }

      const sourceId = data.data?.attributes?.sourceId;
      if (!sourceId) {
        throw new Error('Server did not return a source ID');
      }

      this.args.onSuccess?.();
      this.router.transitionTo('sources.show', sourceId);
    } catch {
      // API unreachable or error — queue for later sync
      await this.offlineQueue.enqueueUrl(this.url);
      this.notifications.info('Added to queue — will sync when available');
      this.isSubmitting = false;
      this.args.onSuccess?.();
      this.router.transitionTo('index');
    }
  }

  async doImageSubmit() {
    if (!this.selectedFile) {
      this.error = 'Please select an image file';
      return;
    }

    if (!this.session.isAuthenticated) {
      this.router.transitionTo('login');
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    // Request push permission for manual submissions (not share intent)
    if (!this.isShareIntentSubmission) {
      const userId = this.session.user?.id;
      if (userId) {
        void this.pushNotifications.requestPermission(userId);
      }
    }

    try {
      const formData = new FormData();
      formData.append('image', this.selectedFile);

      const headers: Record<string, string> = {};
      const authHeader = this.session.authHeader;
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }

      const response = await apiFetch('/api/submissions/image', {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = (await response.json()) as SubmissionResponse;

      if (!response.ok) {
        throw new Error(data.errors?.[0]?.detail ?? 'Failed to upload image');
      }

      const sourceId = data.data?.attributes?.sourceId;
      if (!sourceId) {
        throw new Error('Server did not return a source ID');
      }

      this.setSelectedFile(null);
      this.args.onSuccess?.();
      this.router.transitionTo('sources.show', sourceId);
    } catch {
      // API unreachable or error — queue for later sync
      await this.offlineQueue.enqueueImage(
        this.selectedFile,
        this.selectedFile.type
      );
      this.notifications.info(
        'Image added to queue — will sync when available'
      );
      this.isSubmitting = false;
      this.setSelectedFile(null);
      this.args.onSuccess?.();
      this.router.transitionTo('index');
    }
  }

  get fileName(): string {
    return this.selectedFile?.name ?? '';
  }

  /**
   * Handle image shared via native Android intent (content:// URI).
   * On Android, content:// URIs cannot be fetched by the WebView directly.
   * Capacitor.convertFileSrc() converts them to http://localhost/_capacitor_content_/...
   * which the WebView security model allows.
   */
  async doNativeImageSubmit(uri: string, mimeType: string) {
    if (!this.session.isAuthenticated) {
      this.router.transitionTo('login');
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    try {
      const fetchUri = Capacitor.isNativePlatform()
        ? Capacitor.convertFileSrc(uri)
        : uri;
      // eslint-disable-next-line warp-drive/no-external-request-patterns -- native content:// URI via Capacitor bridge
      const response = await fetch(fetchUri);
      if (!response.ok) {
        throw new Error(`Failed to read shared image (${response.status})`);
      }
      const blob = await response.blob();

      const ext = mimeType.split('/')[1] ?? 'jpg';
      const file = new File([blob], `shared-image.${ext}`, { type: mimeType });
      this.selectedFile = file;
      await this.doImageSubmit();
    } catch {
      // API unreachable or error — queue for later sync
      // Re-read the blob in case doImageSubmit failed after the fetch
      try {
        const fetchUri2 = Capacitor.isNativePlatform()
          ? Capacitor.convertFileSrc(uri)
          : uri;
        // eslint-disable-next-line warp-drive/no-external-request-patterns -- native content:// URI via Capacitor bridge
        const retryRes = await fetch(fetchUri2);
        if (retryRes.ok) {
          const retryBlob = await retryRes.blob();
          await this.offlineQueue.enqueueImage(retryBlob, mimeType);
          this.notifications.info(
            'Image added to queue — will sync when available'
          );
        }
      } catch {
        // Can't even read the image — nothing to queue
      }
      this.isSubmitting = false;
      this.args.onSuccess?.();
      this.router.transitionTo('index');
    }
  }

  <template>
    <div class="tabs tabs-box mb-4">
      <button
        type="button"
        class="tab {{if (isUrlTab this.activeTab) 'tab-active'}}"
        {{on "click" this.switchToUrl}}
      >
        <Icon @icon={{LinkIcon}} class="mr-1 h-4 w-4" />
        URL
      </button>
      <button
        type="button"
        class="tab {{if (isImageTab this.activeTab) 'tab-active'}}"
        {{on "click" this.switchToImage}}
      >
        <Icon @icon={{ImageIcon}} class="mr-1 h-4 w-4" />
        Image
      </button>
    </div>

    {{#if (isUrlTab this.activeTab)}}
      <form {{on "submit" this.submit}}>
        <div class="form-control w-full">
          <label class="label" for="url-input">
            <span class="label-text font-medium">Content URL</span>
          </label>

          {{! URL preview card – shown above input for visibility in constrained mobile drawers }}
          {{#if this.preview}}
            <div
              class="bg-base-200 mb-3 flex items-start gap-3 rounded-lg p-3"
              data-test-url-preview
            >
              {{#if this.preview.thumbnail}}
                <img
                  src={{this.preview.thumbnail}}
                  alt=""
                  class="h-14 w-24 flex-shrink-0 rounded object-cover"
                  loading="lazy"
                />
              {{/if}}
              <div class="min-w-0 flex-1">
                {{#if this.preview.title}}
                  <p class="text-sm leading-snug font-medium">
                    {{this.preview.title}}
                  </p>
                {{/if}}
                {{#if this.preview.description}}
                  <p class="text-base-content/60 mt-0.5 line-clamp-2 text-xs">
                    {{this.preview.description}}
                  </p>
                {{/if}}
              </div>
            </div>
          {{/if}}

          <div class="flex gap-2">
            <input
              id="url-input"
              type="url"
              placeholder="https://www.tiktok.com/@user/video/123..."
              class="input input-bordered flex-1"
              value={{this.url}}
              {{on "input" this.updateUrl}}
              disabled={{this.isSubmitting}}
              required
            />
            <button
              type="button"
              class="btn btn-ghost btn-square"
              title="Paste from clipboard"
              aria-label="Paste from clipboard"
              disabled={{this.isSubmitting}}
              {{on "click" this.pasteUrl}}
            >
              <Icon @icon={{ClipboardPasteIcon}} class="h-5 w-5" />
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={{this.isSubmitting}}
            >
              {{#if this.isSubmitting}}
                <Icon @icon={{LoaderIcon}} class="h-5 w-5 animate-spin" />
                Submitting...
              {{else}}
                Extract
              {{/if}}
            </button>
          </div>
        </div>
      </form>
    {{else}}
      <form {{on "submit" this.submit}}>
        <div class="form-control w-full">
          <label class="label" for="image-input">
            <span class="label-text font-medium">Upload Image</span>
          </label>
          <label
            for="image-input"
            class="flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors
              {{if
                this.isDragOver
                'border-primary bg-primary/10'
                'border-base-300 bg-base-200 hover:bg-base-300'
              }}"
            {{on "dragover" this.handleDragOver}}
            {{on "dragleave" this.handleDragLeave}}
            {{on "drop" this.handleDrop}}
          >
            {{#if this.imageObjectUrl}}
              <img
                src={{this.imageObjectUrl}}
                alt="Preview"
                class="h-24 w-full max-w-48 rounded object-contain"
              />
            {{else}}
              <Icon
                @icon={{UploadIcon}}
                class="h-10 w-10
                  {{if this.isDragOver 'text-primary' 'text-base-content/40'}}"
              />
            {{/if}}
            <p class="text-base-content/60 text-sm">
              {{#if this.fileName}}
                Selected:
                <span class="font-medium">{{this.fileName}}</span>
              {{else if this.isDragOver}}
                Drop to upload
              {{else}}
                Drop image here or click to browse
              {{/if}}
            </p>
            <span class="btn btn-outline btn-sm">
              <Icon @icon={{ImageIcon}} class="h-4 w-4" />
              Choose Image
            </span>
            <input
              id="image-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              class="sr-only"
              {{on "change" this.handleFileSelect}}
              disabled={{this.isSubmitting}}
            />
          </label>
          <div class="mt-4 flex justify-end">
            <button
              type="submit"
              class="btn btn-primary"
              disabled={{this.isSubmitting}}
            >
              {{#if this.isSubmitting}}
                <Icon @icon={{LoaderIcon}} class="h-5 w-5 animate-spin" />
                Extracting...
              {{else}}
                <Icon @icon={{FilmIcon}} class="h-5 w-5" />
                Extract from Image
              {{/if}}
            </button>
          </div>
        </div>
      </form>
    {{/if}}

    {{#if this.error}}
      <p class="text-error mt-2 text-sm">{{this.error}}</p>
    {{/if}}
  </template>
}

function isUrlTab(tab: string): boolean {
  return tab === 'url';
}

function isImageTab(tab: string): boolean {
  return tab === 'image';
}
